import React, { useCallback, useEffect, useState } from "react";
import { withGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Form } from "antd";
import axios from "axios";
import { baseURL, toBasePayURL } from "utils/env";
import { EncryptionPassword, encryptObject } from "utils/encryptDecrypt";
import {
    EcommerceFlow,
    ExclusiveFlow,
    POSFlow,
    POS_DEVICE_WIDTH,
    StatusPending,
    SuiFrensFlow,
    TypeEcommerce,
    TypePOS,
    checkAllRequiredFieldsWithKey,
    encodeContent,
    onEmailAddressVerify,
    onPhoneNumberVerify,
    onZipNumberVerify,
    shortenValue,
    toastMessage,
    useGlobalMerchant,
} from "utils/helper";
import { CryptoPassword, decryptedText } from "utils/crypto";
import ECSBillingComponent from '../ECSuiFrens/ECSxclusiveBilling/ECSBillingComponent';
import ECABillingComponent from '../ECAdvance/ECAdvanceBilling/ECABillingComponent';
import ECEBillingComponent from '../ECExclusive/ECExclusiveBilling/ECEBillingComponent';
import { useBasket } from "containers/POSContainer/constant";
import { formFields, ecNextFormFields } from "./constant";

const ECAddressComponent = ({ flowName, setShowShippingLayout, googleReCaptchaProps }: any) => {
    const { itemList } = useBasket();
    const { globalMerchantId, globalMerchantCurrency, setPaymentIframeURL } = useGlobalMerchant();

    const [form] = Form.useForm();
    const [nextForm] = Form.useForm();
    const [onBtnLoader, setOnBtnLoader] = useState(false);
    const [isBothSame, setIsBothSame] = useState(true);
    const [titleName, setTitleName] = useState("");
    const [errors, setErrors] = useState<any>();
    const [formData, setFormData] = useState<any>({ ...formFields });
    const [nextFormData, setNextFormData] = useState<any>({ ...ecNextFormFields, });

    const onErrorCall = (getErrorMessage: any, getContentData: any) => {
        if (getErrorMessage == "Failed reCAPTCHA verification") {
            getToken(getContentData);
        } else {
            toastMessage(getErrorMessage);
            setOnBtnLoader(false);
        }
    }

    const createAddressObject = (getContentData: any, nextFormData: any, isBothSame: boolean, getOrderId: any) => ({
        sfirst_name: isBothSame ? getContentData.first_name : nextFormData.sfirst_name,
        slast_name: isBothSame ? getContentData.last_name : nextFormData.slast_name,
        saddress_one: isBothSame ? getContentData.address_one : nextFormData.saddress_one,
        saddress_two: isBothSame ? getContentData.address_two : nextFormData.saddress_two,
        scity: isBothSame ? getContentData.city : nextFormData.scity,
        sstate: isBothSame ? getContentData.state : nextFormData.sstate,
        szip_code: isBothSame ? getContentData.zip_code : nextFormData.szip_code,
        scountry: isBothSame ? getContentData.country : nextFormData.scountry,
        sphone: isBothSame ? getContentData.phone : nextFormData.sphone,
        semail: isBothSame ? getContentData.email : nextFormData.semail,
        order_id: getOrderId,
        same_as_billing: isBothSame ? "yes" : "no",
        merchant_id: globalMerchantId,
    });

    const handleAxiosResponse = (response: any, getOrderId: any) => {
        const responseData = response.data || response;
        if (responseData?.success) {
            const orderType = titleName === POSFlow ? `pos=${getOrderId}` : `ecommerce=${getOrderId}`;
            const generateKey = encodeContent(orderType);
            setOnBtnLoader(false);
            window.innerWidth == POS_DEVICE_WIDTH ? setPaymentIframeURL(`${toBasePayURL}/${generateKey}`) : window.location.replace(`${toBasePayURL}/${generateKey}`)
        } else { toastMessage("Something went wrong"); }
    };

    const handleAxiosError = (error: any, formData: any) => {
        error?.message && onErrorCall(error.message, formData);
    };

    const onStoreAddressDetails = async (getOrderId: any, getContentData: any) => {
        const newObject = createAddressObject(getContentData, nextFormData, isBothSame, getOrderId);
        try {
            const encryptData = await encryptObject({ ...getContentData, ...newObject }, EncryptionPassword);
            const response = await axios.post(`${baseURL}add_billing_details`, { raw_data: encryptData });
            handleAxiosResponse(response, getOrderId);
        } catch (error: any) { handleAxiosError(error, getContentData); }
    };

    const modifyItemList = (itemList: any) =>
        itemList.map((item: any) => ({
            categories: item.product.categories,
            img: item.product.img,
            price: item.product.price,
            product_id: item.product.product_id,
            name: item.product.product_name,
            sku: item.product.sku,
            oqty: item.qty,
        }));

    const onStoreData = async (getToken: any, getContentData: any) => {
        if (grandTotal === 0) return;

        setOnBtnLoader(true);
        const modifyResp = modifyItemList(itemList);
        const getWalletEmail = localStorage.getItem("ec-email");
        const newObject = {
            merchant_id: globalMerchantId,
            currency: globalMerchantCurrency,
            amount: shortenValue(grandTotal),
            name: " ",
            client_address: " ",
            date_time: " ",
            digest: " ",
            product_detail: modifyResp,
            status: StatusPending,
            merchant_email: decryptedText(CryptoPassword, getWalletEmail),
            order_type: titleName === POSFlow ? TypePOS : TypeEcommerce,
            recaptchaToken: getToken,
        };

        try {
            const encryptData = await encryptObject(newObject, EncryptionPassword);
            const response = await axios.post(`${baseURL}merchant_orders`, { raw_data: encryptData });

            if (response?.data?.order_id) {
                onStoreAddressDetails(response.data.order_id, getContentData);
            } else {
                onErrorCall(response?.data?.message || "Error processing request", getContentData);
            }
        } catch (error: any) { handleAxiosError(error, getContentData); }
    };

    const checkAllErrors = (getFormFields: any, getContentData: any) => {
        let errorObject = { ...checkAllRequiredFieldsWithKey(getFormFields, getContentData), };
        setErrors({ ...errors, ...errorObject });
        return Object.values(errorObject).every((errorMsg) => !errorMsg);
    };

    const getToken = useCallback(async (getContentData: any) => {
        if (googleReCaptchaProps && googleReCaptchaProps.executeRecaptcha) {
            try {
                const _token = await googleReCaptchaProps.executeRecaptcha("homepage");
                onStoreData(_token, getContentData);
            } catch (error) { console.error('Error while executing recaptcha:', error); }
        } else { console.error('googleReCaptchaProps.executeRecaptcha is not available.'); }
    }, [googleReCaptchaProps]);

    const validateFormData = (data: any, fieldPrefix: string) => {
        const errors: { name: string; message: string }[] = [];
        const emailValidation = onEmailAddressVerify(data[`${fieldPrefix}email`]);
        if (!emailValidation.status) { errors.push({ name: `${fieldPrefix}email`, message: emailValidation.message }); }
        const phoneValidation = onPhoneNumberVerify(data[`${fieldPrefix}phone`]);
        if (!phoneValidation.status) { errors.push({ name: `${fieldPrefix}phone`, message: phoneValidation.message }); }
        const zipValidation = onZipNumberVerify(data[`${fieldPrefix}zip_code`]);
        if (!zipValidation.status) { errors.push({ name: `${fieldPrefix}zip_code`, message: zipValidation.message }); }
        return errors;
    };

    const setFormErrors = (errors: { name: string; message: string }[]) => {
        const errorMap = errors.reduce((acc, { name, message }) => {
            acc[name] = message;
            return acc;
        }, {} as { [key: string]: string });
        setErrors({ ...errors, ...errorMap });
    };

    const onCheckSubErrors = (): boolean => {
        const formErrors = validateFormData(formData, '');
        if (formErrors.length > 0) {
            setFormErrors(formErrors);
            return false;
        }
        if (!isBothSame) {
            const nextFormErrors = validateFormData(nextFormData, 's');
            if (nextFormErrors.length > 0) {
                setFormErrors(nextFormErrors);
                return false;
            }
        }
        return true;
    };

    const onCheckout = async () => {
        const isFormValid = checkAllErrors(formFields, formData);
        const isNextFormValid = isBothSame || checkAllErrors(ecNextFormFields, nextFormData);
        isFormValid && isNextFormValid && onCheckSubErrors() && getToken(formData);
    };

    function onChange(e: any) { setIsBothSame(!isBothSame); }

    const totalAmounts = itemList.map((item: any) => { return item.qty * item.product.price; });
    const grandTotal = totalAmounts.reduce((acc: any, amount: any) => acc + amount, 0);

    function getPathType(pathname: any) {
        if (pathname.startsWith("/pos/")) {
            return POSFlow;
        } else if (pathname.startsWith("/")) {
            return EcommerceFlow;
        } else {
            return "";
        }
    }

    useEffect(() => {
        const pathname = window.location.pathname;
        if (pathname === "/order") {
            setTitleName(EcommerceFlow);
            // globalMerchantId && setMerchantId(globalMerchantId.toString());
        } else {
            const orderType = getPathType(pathname);
            setTitleName(orderType);
            const parts = pathname.split("/");
            const number = parts[parts.length - 1];
            // setMerchantId(atob(number));
        }
    }, []);

    return (
        <>
            {flowName === SuiFrensFlow && (
                <ECSBillingComponent
                    setShowShippingLayout={setShowShippingLayout}
                    form={form}
                    isBothSame={isBothSame}
                    onChange={onChange}
                    formData={formData}
                    setFormData={setFormData}
                    errors={errors}
                    setErrors={setErrors}
                    nextForm={nextForm}
                    nextFormData={nextFormData}
                    setNextFormData={setNextFormData}
                    itemList={itemList}
                    grandTotal={grandTotal}
                    onCheckout={onCheckout}
                    onBtnLoader={onBtnLoader}
                />
            )}
            {flowName == ExclusiveFlow && (
                <ECEBillingComponent
                    setShowShippingLayout={setShowShippingLayout}
                    form={form}
                    isBothSame={isBothSame}
                    onChange={onChange}
                    formData={formData}
                    setFormData={setFormData}
                    errors={errors}
                    setErrors={setErrors}
                    nextForm={nextForm}
                    nextFormData={nextFormData}
                    setNextFormData={setNextFormData}
                    itemList={itemList}
                    grandTotal={grandTotal}
                    onCheckout={onCheckout}
                    onBtnLoader={onBtnLoader}
                />
            )}
            {flowName == EcommerceFlow && (
                <div className='ecContainer'>
                    <ECABillingComponent
                        setShowShippingLayout={setShowShippingLayout}
                        form={form}
                        isBothSame={isBothSame}
                        onChange={onChange}
                        formData={formData}
                        setFormData={setFormData}
                        errors={errors}
                        setErrors={setErrors}
                        nextForm={nextForm}
                        nextFormData={nextFormData}
                        setNextFormData={setNextFormData}
                        itemList={itemList}
                        grandTotal={grandTotal}
                        onCheckout={onCheckout}
                        onBtnLoader={onBtnLoader}
                    />
                </div>
            )}
        </>
    )
}

export default withGoogleReCaptcha(ECAddressComponent)